import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import useDraftState from '@/creditflowresearch/application/hooks/useDraftState';
import useDraftChangeNotifier from '@/creditflowresearch/ui/common/hooks/sidebar/useDraftChangeNotifier';
import useFilterBoundaries from './useFilterBoundaries';

export interface IApplyOptions {
  skipClose?: boolean,
}

interface IBondScreenerBoundaries<TBoundaryRegion> {
  regions: TBoundaryRegion[],
}

interface ICreateUseBondScreenerSidebarFilterOptions<TFilters> {
  filterType: 'corp' | 'abs',
  defaultFilters: TFilters,
  resetPreservedKeys: (keyof TFilters)[],
}

interface IUseBondScreenerSidebarFilterProps<TFilters, TBoundaryRegion> {
  isOpen: boolean,
  payload: TFilters,
  onApply: (filters: TFilters, boundaryRegions: TBoundaryRegion[], options?: IApplyOptions) => void,
  defaultFilters?: TFilters,
}

const createUseBondScreenerSidebarFilter = <
  TFilters extends Record<string, unknown>,
  TBoundaries extends IBondScreenerBoundaries<TBoundaryRegion>,
  TBoundaryRegion,
>(
  { filterType, defaultFilters, resetPreservedKeys }: ICreateUseBondScreenerSidebarFilterOptions<TFilters>,
) => (
  { isOpen, payload, onApply, defaultFilters: configDefaultFilters }:
    IUseBondScreenerSidebarFilterProps<TFilters, TBoundaryRegion>,
) => {
  const resetDefaults = configDefaultFilters ?? defaultFilters;
  const { filters: rawFilters, isFetching } = useFilterBoundaries(isOpen, filterType);
  const filters = rawFilters as unknown as TBoundaries | null;

  const [hasValidationError, setHasValidationError] = useState(false);
  const [emptyRequiredFilters, setEmptyRequiredFilters] = useState<Record<string, boolean>>({});
  const [activeSectionIds, setActiveSectionIds] = useState<string[]>([]);

  const setRequiredFilterEmpty = useCallback((key: string, isEmpty: boolean): void => {
    setEmptyRequiredFilters((prev) => (prev[key] === isEmpty ? prev : { ...prev, [key]: isEmpty }));
  }, []);

  const { draft: localFilters, getDraft, setDraft, resetTo: baseResetTo, resetKey } = useDraftState(payload);
  const { notifyDraftChanged, subscribeToDraftChanges } = useDraftChangeNotifier();

  const setLocalFilters: typeof setDraft = useCallback((updater) => {
    setDraft(updater);
    notifyDraftChanged();
  }, [setDraft, notifyDraftChanged]);

  const resetTo: typeof baseResetTo = useCallback((value) => {
    baseResetTo(value);
    notifyDraftChanged();
  }, [baseResetTo, notifyDraftChanged]);

  useEffect(() => {
    if (isEqual(getDraft(), payload)) return;
    resetTo(payload);
  }, [isOpen, payload, getDraft, resetTo]);

  const apply = useCallback((options?: IApplyOptions): void => {
    const current = getDraft();
    setDraft(current);
    notifyDraftChanged();
    onApply(current, filters?.regions ?? [], options);
  }, [getDraft, setDraft, notifyDraftChanged, onApply, filters?.regions]);

  const reset = useCallback((): void => {
    const preserved = resetPreservedKeys.reduce<Partial<TFilters>>(
      (acc, key) => ({ ...acc, [key]: payload[key] }),
      {},
    );
    resetTo({ ...resetDefaults, ...preserved });
    setHasValidationError(false);
    setActiveSectionIds([]);
  }, [resetTo, payload, resetDefaults]);

  const discard = useCallback((): void => {
    resetTo(payload);
    setHasValidationError(false);
  }, [payload, resetTo]);

  return useMemo(() => ({
    filters,
    isFetching,
    localFilters,
    getDraft,
    setLocalFilters,
    subscribe: subscribeToDraftChanges,
    apply,
    reset,
    resetTo,
    discard,
    resetKey,
    isOpen,
    hasValidationError,
    setHasValidationError,
    emptyRequiredFilters,
    setRequiredFilterEmpty,
    activeSectionIds,
    setActiveSectionIds,
    payload,
  }), [
    filters, isFetching, localFilters, getDraft, setLocalFilters, subscribeToDraftChanges,
    apply, reset, resetTo, discard, resetKey, isOpen, hasValidationError, setHasValidationError,
    emptyRequiredFilters, setRequiredFilterEmpty, activeSectionIds, setActiveSectionIds, payload,
  ]);
};

export default createUseBondScreenerSidebarFilter;
